import React, { useState, useRef } from 'react';
import { ProgressBar, Table } from 'react-bootstrap';
import styles from './ItemPricesCSVTool.module.css';
import ValidationFeedback from '../../components/ValidationFeedback/ValidationFeedback';
import { UpdateItemPrices } from '../../libs/GraphQL';

const VALIDATION_TYPE = {
  ERRORS: 'errors',
  WARNINGS: 'warnings',
  SUCCESS: 'success',
};

const BATCH_SIZE = 500;

const isEmpty = (value) => {
  return value.trim() === '';
};

const ItemPricesCSVTool = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [invalidRows, setInvalidRows] = useState([]);
  const [rowErrorsFromBackend, setRowErrorsFromBackend] = useState([]);
  const csvFileRef = useRef(null);

  // Helper to get all errors for a row (frontend and backend)
  const getAllErrorsForRow = (row, rowIndex) => {
    const frontend = rowErrors[rowIndex] || [];
    const backendRow = rowErrorsFromBackend.find(
      (r) => r.schemaId === row.schemaId && r.item === row.item,
    );
    const backend = backendRow ? backendRow.errors : [];
    return { frontend, backend };
  };

  // Helper to determine if a row is invalid (frontend or backend errors)
  const isRowInvalid = (row, rowIndex) => {
    const { frontend, backend } = getAllErrorsForRow(row, rowIndex);
    return frontend.length > 0 || backend.length > 0;
  };

  const validatePrice = (price) => {
    const numPrice = parseFloat(price);
    // Only allow numbers >= 0 and at most 2 decimal places
    return (
      !Number.isNaN(numPrice) &&
      numPrice >= 0 &&
      /^\d+(\.\d{1,2})?$/.test(price)
    );
  };

  const getDataFromCsv = (csvText) => {
    const rows = csvText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);
    if (rows.length === 0) return { headers: [], rows: [] };
    const headers = rows.shift().split(',');
    return { headers, rows };
  };

  const validateCsvContent = (csvText) => {
    setFeedback(null);
    setErrors([]);
    setRowErrors({});
    setInvalidRows([]);
    setRowErrorsFromBackend([]); // Clear backend errors from previous upload

    const csvErrors = [];
    const { headers, rows } = getDataFromCsv(csvText);
    const newRowErrors = {};
    const keyToRows = new Map(); // key: schemaId|item, value: array of {rowIndex, lineNumber}
    const parsedRowsTemp = [];
    const invalidRowsArr = [];

    if (rows.length === 0) {
      csvErrors.push({
        title: `Line 1`,
        description:
          'No rows found. Provide at least one row with schemaId, item, and price.',
      });
      setErrors(csvErrors);
      setInvalidRows([]);
      return;
    }

    // Validate headers
    const requiredHeaders = ['schemaId', 'item', 'price'];
    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.includes(header),
    );
    if (missingHeaders.length > 0) {
      csvErrors.push({
        title: 'Header Validation',
        description: `Missing required columns: ${missingHeaders.join(', ')}`,
      });
      setInvalidRows([]);
      return;
    }

    // Parse and validate each row (first pass: basic validation, collect keys)
    rows.forEach((row, rowIndex) => {
      const columns = row.split(',');
      const [schemaIdRaw, itemRaw, priceRaw] = columns;
      const schemaId = schemaIdRaw ? schemaIdRaw.trim() : '';
      const item = itemRaw ? itemRaw.trim() : '';
      const price = priceRaw ? priceRaw.trim() : '';
      const lineNumber = rowIndex + 2;
      const key = `${schemaId}|${item}`;
      let rowErrors = [];
      let errorReason = '';

      if (isEmpty(schemaId)) {
        rowErrors.push('schemaId cannot be empty');
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description: 'schemaId cannot be empty.',
        });
        errorReason += 'schemaId cannot be empty; ';
      }
      if (isEmpty(item)) {
        rowErrors.push('item cannot be empty');
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description: 'item cannot be empty.',
        });
        errorReason += 'item cannot be empty; ';
      } else if (!/^[A-Z0-9_]+$/.test(item)) {
        rowErrors.push(
          'item must be all uppercase letters, numbers, or underscores (no spaces or special characters)',
        );
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description:
            'item must be all uppercase letters, numbers, or underscores (no spaces or special characters).',
        });
        errorReason +=
          'item must be all uppercase letters, numbers, or underscores; ';
      }
      if (!validatePrice(price)) {
        rowErrors.push(
          'price must be a valid number with at most 2 decimal places and greater than or equal to 0',
        );
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description:
            'price must be a valid number with at most 2 decimal places and greater than or equal to 0.',
        });
        errorReason +=
          'price must be a valid number >= 0 and at most 2 decimal places';
      }

      // Collect all row indices for each key
      if (!isEmpty(schemaId) && !isEmpty(item)) {
        if (!keyToRows.has(key)) {
          keyToRows.set(key, []);
        }
        keyToRows.get(key).push({ rowIndex, lineNumber });
      }

      parsedRowsTemp.push({
        schemaId,
        item,
        price,
        lineNumber,
        rowErrors,
        errorReason,
      });
    });

    // Second pass: mark all duplicates
    keyToRows.forEach((rowInfos, key) => {
      if (rowInfos.length > 1) {
        const allLines = rowInfos.map((info) => info.lineNumber).join(', ');
        // One banner error per duplicate group
        csvErrors.push({
          title: 'Duplicate Entry',
          description: `Duplicate schemaId + item combination on lines ${allLines}.`,
        });
        // Mark each row in the group
        rowInfos.forEach(({ rowIndex, lineNumber }) => {
          const otherLines = rowInfos
            .filter((info) => info.lineNumber !== lineNumber)
            .map((info) => info.lineNumber)
            .join(', ');
          const duplicateMsg = `Duplicate schemaId + item combination (also found in line${
            otherLines.includes(',') ? 's' : ''
          } ${otherLines})`;
          parsedRowsTemp[rowIndex].rowErrors.push(duplicateMsg);
          parsedRowsTemp[rowIndex].errorReason += duplicateMsg + '; ';
        });
      }
    });

    // Build parsedRows and invalidRowsArr for UI
    const parsedRows = parsedRowsTemp.map((row, rowIndex) => {
      if (row.rowErrors.length > 0) {
        newRowErrors[rowIndex] = row.rowErrors;
        invalidRowsArr.push({
          schemaId: row.schemaId,
          item: row.item,
          price: row.price,
          lineNumber: row.lineNumber,
          errorReason: row.errorReason.trim(),
        });
      }
      return {
        schemaId: row.schemaId,
        item: row.item,
        price: row.price,
        lineNumber: row.lineNumber,
        isValid: row.rowErrors.length === 0,
      };
    });

    setRowErrors(newRowErrors);
    setErrors(csvErrors);
    setInvalidRows(invalidRowsArr);
    setParsedData({ headers, rows: parsedRows });

    // Show success message if no frontend errors
    if (csvErrors.length === 0) {
      const validRows = parsedRows.filter((row) => row.isValid);
      const batchCount = Math.ceil(validRows.length / BATCH_SIZE);
      const batchMessage =
        validRows.length > BATCH_SIZE
          ? ` Upload will be processed in ${batchCount} batches (${BATCH_SIZE} items per batch).`
          : '';

      setFeedback({
        type: VALIDATION_TYPE.SUCCESS,
        data: [
          {
            title: 'Validation Complete',
            description: `File validated successfully. ${
              validRows.length
            } item${
              validRows.length !== 1 ? 's' : ''
            } ready to upload.${batchMessage}`,
          },
        ],
      });
    }
  };

  const changeFileHandler = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setCsvFile(null);
      setParsedData(null);
      setErrors([]);
      setRowErrors({});
      setInvalidRows([]);
      setRowErrorsFromBackend([]);
      return;
    }
    setCsvFile(file);
    setParsedData(null);
    setRowErrorsFromBackend([]);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      validateCsvContent(content);
    };
    reader.readAsText(file);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setFeedback(null);
    setRowErrorsFromBackend([]);
    if (!csvFile || errors.length || !parsedData) return;

    const input = parsedData.rows
      .filter((r) => r.isValid)
      .map((r) => ({
        schemaId: r.schemaId,
        item: r.item,
        price: parseFloat(r.price),
      }));

    setLoading(true);
    setUploadProgress(0);
    try {
      if (input.length === 0) {
        setLoading(false);
        setFeedback({
          type: VALIDATION_TYPE.WARNINGS,
          data: [
            {
              title: 'No Updates',
              description: 'No valid rows found in the CSV file.',
            },
          ],
        });
        return;
      }

      // Batch processing to avoid Lambda timeout
      const batches = [];
      for (let i = 0; i < input.length; i += BATCH_SIZE) {
        batches.push(input.slice(i, i + BATCH_SIZE));
      }

      // Process batches sequentially with progress updates
      let totalUpdated = 0;
      let allRowErrors = [];
      let batchLevelErrors = [];
      const updatedSchemas = new Set();

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const progress = ((i + 1) / batches.length) * 100;
        setUploadProgress(Math.round(progress));

        // Allow UI to update and remain responsive
        await new Promise((resolve) => setTimeout(resolve, 0));

        try {
          const batchResult = await UpdateItemPrices(batch);

          if (batchResult.success) {
            const batchErrors = batchResult.rowErrors?.length || 0;
            totalUpdated += batch.length - batchErrors;
            if (batchResult.updatedSchemas) {
              batchResult.updatedSchemas.forEach((s) => updatedSchemas.add(s));
            }
          }

          if (batchResult.rowErrors && batchResult.rowErrors.length > 0) {
            allRowErrors = allRowErrors.concat(batchResult.rowErrors);
          }
        } catch (error) {
          // If a batch fails, collect as a batch-level error (shown as banner)
          const errorMsg =
            error?.errors?.[0]?.message || error?.message || String(error);

          if (errorMsg.includes('timed out') || errorMsg.includes('timeout')) {
            batchLevelErrors.push(
              `Batch ${
                i + 1
              } timed out. Please split your CSV into smaller files.`,
            );
          } else if (errorMsg.includes('payload size exceeded')) {
            batchLevelErrors.push(
              `Batch ${
                i + 1
              } payload too large. Please split your CSV into smaller files.`,
            );
          } else {
            batchLevelErrors.push(`Batch ${i + 1} failed: ${errorMsg}`);
          }
        }
      }

      setUploadProgress(100);
      setLoading(false);

      // Handle results
      const hasRowErrors = allRowErrors.length > 0;
      const hasBatchErrors = batchLevelErrors.length > 0;

      if (hasRowErrors || hasBatchErrors) {
        setRowErrorsFromBackend(allRowErrors);
        const feedbackItems = [];

        if (totalUpdated > 0) {
          feedbackItems.push({
            title: 'Partial Success',
            description: `Successfully updated ${totalUpdated} item${
              totalUpdated !== 1 ? 's' : ''
            }.`,
          });
        }

        if (hasBatchErrors) {
          batchLevelErrors.forEach((msg) => {
            feedbackItems.push({
              title: 'Batch Error',
              description: msg,
            });
          });
        }

        if (hasRowErrors) {
          feedbackItems.push({
            title: 'Row Errors',
            description: `${allRowErrors.length} row${
              allRowErrors.length !== 1 ? 's' : ''
            } had errors. See table below for details.`,
          });
        }

        setFeedback({
          type:
            totalUpdated > 0
              ? VALIDATION_TYPE.WARNINGS
              : VALIDATION_TYPE.ERRORS,
          data: feedbackItems,
        });
      } else {
        // Complete success
        const schemaCount = updatedSchemas.size;
        setFeedback({
          type: VALIDATION_TYPE.SUCCESS,
          data: [
            {
              title: 'Success',
              description: `Successfully updated ${totalUpdated} item${
                totalUpdated !== 1 ? 's' : ''
              } across ${schemaCount} schema${
                schemaCount !== 1 ? 's' : ''
              } in ${batches.length} batch${
                batches.length !== 1 ? 'es' : ''
              } (${BATCH_SIZE} items per batch).`,
            },
          ],
        });
        // Clear file input but keep parsedData visible for sanity check
        setCsvFile(null);
        if (csvFileRef.current) {
          csvFileRef.current.value = null;
        }
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      const errorMsg =
        error?.errors?.[0]?.message || error?.message || String(error);
      setLoading(false);
      // Clear file ref so re-selecting the same file triggers onChange
      if (csvFileRef.current) {
        csvFileRef.current.value = null;
      }
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: 'Upload Error',
            description: `Failed to process upload: ${errorMsg}. ${
              input.length > BATCH_SIZE
                ? 'For large files, please ensure your CSV is properly formatted.'
                : ''
            }`,
          },
        ],
      });
    }
  };

  const downloadAllInvalidRows = () => {
    // Combine frontend and backend errors for download
    const allErrorRows = [];

    // Add frontend validation errors
    invalidRows.forEach((row) => {
      allErrorRows.push({
        schemaId: row.schemaId,
        item: row.item,
        price: row.price,
        lineNumber: row.lineNumber,
        errorType: 'Frontend Validation',
        errorReason: row.errorReason,
      });
    });

    // Add backend errors (from upload attempt)
    if (parsedData && parsedData.rows) {
      rowErrorsFromBackend.forEach((backendError) => {
        // Find matching row in parsedData if possible
        const matchingRow = parsedData.rows.find(
          (r) =>
            r.schemaId === backendError.schemaId &&
            r.item === backendError.item,
        );

        const errorReasons = Array.isArray(backendError.errors)
          ? backendError.errors.join('; ')
          : String(backendError.errors || 'Unknown error');

        allErrorRows.push({
          schemaId: backendError.schemaId,
          item: backendError.item,
          price: backendError.price || (matchingRow ? matchingRow.price : ''),
          lineNumber: matchingRow ? matchingRow.lineNumber : 'N/A',
          errorType: 'Backend Validation',
          errorReason: errorReasons,
        });
      });
    }

    if (allErrorRows.length === 0) return;

    const csvContent = [
      'schemaId,item,price,lineNumber,errorType,errorReason',
      ...allErrorRows.map((row) =>
        [
          row.schemaId,
          row.item,
          row.price,
          row.lineNumber,
          row.errorType,
          `"${row.errorReason.replace(/"/g, '""')}"`,
        ].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'invalid_rows.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const invalidCount = invalidRows.length;
  const totalErrorCount = invalidCount + rowErrorsFromBackend.length;

  return (
    <main className={styles.container}>
      {/* Feedback at the top for immediate visibility */}
      {feedback ? (
        <ValidationFeedback data={feedback.data} type={feedback.type} />
      ) : null}

      <h1>Base Price CSV Upload</h1>
      <form className={styles.form} onSubmit={submitHandler}>
        <div className={styles.formWrapper}>
          <div className={styles.flexColumn}>
            <label htmlFor="csvFileInput" className={styles.label}>
              Upload CSV file
            </label>
            <span className={styles.fileInfo}>
              {' '}
              File must contain columns: <b>schemaId</b>, <b>item</b>,{' '}
              <b>price</b>
            </span>
            <input
              id="csvFileInput"
              type="file"
              accept=".csv"
              onChange={changeFileHandler}
              ref={csvFileRef}
              style={{ cursor: 'pointer' }}
            />
          </div>
          {totalErrorCount > 0 ? (
            <button
              className={styles.button}
              onClick={downloadAllInvalidRows}
              type="button"
            >
              Download {totalErrorCount} Error{totalErrorCount !== 1 ? 's' : ''}{' '}
              as CSV
            </button>
          ) : (
            <button
              className={styles.button}
              disabled={errors.length || !csvFile || loading}
              type="submit"
            >
              {loading
                ? 'Uploading...'
                : parsedData && parsedData.rows
                ? `Upload ${
                    parsedData.rows.filter((r) => r.isValid).length
                  } Item${
                    parsedData.rows.filter((r) => r.isValid).length !== 1
                      ? 's'
                      : ''
                  }`
                : 'Validate & Upload'}
            </button>
          )}
        </div>
      </form>

      {loading && (
        <div className={styles.progressBarContainer}>
          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
        </div>
      )}

      {parsedData && parsedData.rows && parsedData.rows.length > 0 && (
        <div className={styles.tableContainer}>
          <h3>CSV Data Preview</h3>
          <div className={styles.summary}>
            <p>
              <strong>Summary:</strong> {parsedData.rows.length} total rows,{' '}
              <span style={{ color: '#28a745', fontWeight: 600 }}>
                {
                  parsedData.rows.filter((row, idx) => !isRowInvalid(row, idx))
                    .length
                }{' '}
                valid
              </span>
              ,{' '}
              <span
                style={{
                  color: totalErrorCount > 0 ? '#dc3545' : 'black',
                  fontWeight: 600,
                }}
              >
                {
                  parsedData.rows.filter((row, idx) => isRowInvalid(row, idx))
                    .length
                }{' '}
                invalid
              </span>
            </p>
          </div>
          <div className={styles.tableWrapper}>
            <Table>
              <thead>
                <tr>
                  <th>Line #</th>
                  <th className="schemaCol">schemaId</th>
                  <th className="itemCol">item</th>
                  <th className="priceCol">price</th>
                  <th>Validation</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.rows.map((row, rowIndex) => {
                  const { frontend, backend } = getAllErrorsForRow(
                    row,
                    rowIndex,
                  );
                  const invalid = isRowInvalid(row, rowIndex);
                  const rowClass = invalid ? styles.errorRow : styles.validRow;
                  return (
                    <tr key={rowIndex} className={rowClass}>
                      <td>{row.lineNumber}</td>
                      <td className="schemaCol">{row.schemaId}</td>
                      <td className="itemCol">{row.item}</td>
                      <td className="priceCol">{row.price}</td>
                      <td>
                        {[...frontend, ...backend].length > 0 ? (
                          <ul className={styles.errorList}>
                            {frontend.length > 0 && (
                              <>
                                <li
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: 4,
                                    color: '#dc3545',
                                  }}
                                >
                                  Frontend:
                                </li>
                                {frontend.map((error, errorIndex) => (
                                  <li
                                    key={`fe-${errorIndex}`}
                                    className={styles.errorItem}
                                  >
                                    <span className={styles.errorDot}></span>
                                    {error}
                                  </li>
                                ))}
                              </>
                            )}
                            {backend.length > 0 && (
                              <>
                                <li
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: 4,
                                    marginTop: 8,
                                    color: '#dc3545',
                                  }}
                                >
                                  Backend:
                                </li>
                                {backend.map((error, errorIndex) => (
                                  <li
                                    key={`be-${errorIndex}`}
                                    className={styles.errorItem}
                                  >
                                    <span className={styles.errorDot}></span>
                                    {error}
                                  </li>
                                ))}
                              </>
                            )}
                          </ul>
                        ) : (
                          <span className={styles.validText}>✓ Valid</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </main>
  );
};

export default ItemPricesCSVTool;
